import { stopEvent } from 'utils/event';

export type UseHighlightedIndexOptions<TData> = Readonly<{
    /**
     * The current highlighted index.
     */
    highlightedIndex: number;
    /**
     * Item list.
     */
    items: TData[] | undefined;
    /**
     * Callback for when item is selected using `[Enter]` key.
     */
    onSelect: (result: TData) => void;
    /**
     * Disabled key handlers.
     */
    isDisabled?: boolean;

    /**
     * Callback to handle when highlighted index changes.
     *
     * @param boundaMetadata information about whether the keyboard selection
     * would have placed the selection outside the bounds of the `items`
     */
    onChange: (
        highlightedIndex: number,
        boundsMetadata: undefined | 'beforeFirst' | 'afterLast'
    ) => void;

    /**
     * whether 'Space' should submit (otherwise just 'Enter' does)
     */
    spaceSubmits?: boolean;
}>;

/**
 * Hook that returns a key event handler to handle
 * changing the highlighted index and item selection.
 */
export function useHighlightedIndex<TData>({
    highlightedIndex,
    items,
    onSelect,
    onChange,
    isDisabled,
    spaceSubmits,
}: UseHighlightedIndexOptions<TData>) {
    return (event: KeyboardEvent | React.KeyboardEvent) => {
        if (isDisabled) {
            return;
        }
        if (items && items.length > 0) {
            if (event.code === 'ArrowUp') {
                const [idx, meta] =
                    highlightedIndex > 0
                        ? [highlightedIndex - 1, undefined]
                        : ([highlightedIndex, 'beforeFirst'] as const);
                onChange(idx, meta);
                stopEvent(event);
            } else if (event.code === 'ArrowDown') {
                const [idx, meta] =
                    highlightedIndex < items.length - 1
                        ? [highlightedIndex + 1, undefined]
                        : ([highlightedIndex, 'afterLast'] as const);

                onChange(idx, meta);
                stopEvent(event);
            } else if (
                (event.code === 'Enter' || (spaceSubmits && event.code === 'Space')) &&
                items[highlightedIndex]
            ) {
                onSelect(items[highlightedIndex]);
                stopEvent(event);
            }
        }
    };
}
